import { Controller } from "@hotwired/stimulus"
import Sortable from "sortablejs"
import { track } from "../lib/mixpanel"

// Full G702-style sub pay request page (Phelipe 2026-08-17) — mirrors the
// builder pay application: per-line billing inputs recompute row totals,
// %, balances and the summary card on every keystroke. The purple AI zone
// prefills the $ column from a dropped invoice; typing works without one.
export default class extends Controller {
  static targets = ["dropZone", "fileInput", "chips", "readStatus", "submitBtn", "deleteModal", "deleteBtn",
                    "signModal", "certAgree", "signSubmit", "signRows", "signRowTemplate",
                    "prevBand", "prevChevron", "saveStatus", "attachChips", "attachInput",
                    "withdrawModal", "withdrawBtn",
                    "removeFileModal", "removeFileBtn", "removeFileName",
                    "reviseModal", "reviseComment", "reviseBtn", "reviseError",
                    "feeBody", "extraBody", "extraBand", "extraTemplate", "pendingNote"]
  static values = { createUrl: String, updateUrl: String, invoiceId: Number, backUrl: String, identityUrl: String,
                    presignUrl: String, readUrl: String, asNeeded: { type: Boolean, default: false },
                    retainage: Number, original: Number, netChange: Number, prevCerts: Number,
                    reorderUrl: String }

  connect() {
    this._extraFiles = []
    this._extraSeq = 0
    this.recalc()
    this._initPrevCols()
    this._initSortables()
    // An action that reloaded this page (removing a file) left its word here.
    const pending = sessionStorage.getItem("builder-finance.flash")
    if (pending) {
      sessionStorage.removeItem("builder-finance.flash")
      this._toast(pending)
    }
  }

  // ── Previous column group collapse — same behavior (and same saved
  // choice) as the GC pay app grid: band click hides QTY and %, keeps $.
  togglePrevCols() {
    this._applyPrevCols(!this._prevCollapsed)
    try { localStorage.setItem("builder-pr.prev-cols", this._prevCollapsed ? "collapsed" : "open") } catch {}
  }

  _initPrevCols() {
    this._prevCollapsed = false
    if (!this.hasPrevBandTarget) return
    let saved = null
    try { saved = localStorage.getItem("builder-pr.prev-cols") } catch {}
    if (saved === "collapsed") this._applyPrevCols(true)
  }

  _applyPrevCols(collapsed) {
    this._prevCollapsed = collapsed
    this.prevBandTarget.colSpan = collapsed ? 1 : 3
    if (this.hasPrevChevronTarget) this.prevChevronTarget.style.transform = collapsed ? "rotate(-90deg)" : ""
    this.element.querySelectorAll("[data-prev-col]").forEach((el) => { el.hidden = collapsed })
  }

  // Spreadsheet-style keyboard travel, same as the GC grid: ↑/↓ (and Enter ↓)
  // move to the same field on the neighboring row; ←/→ hop between fields
  // only when the caret sits at the edge of the text. Landing selects.
  gridKeydown(e) {
    const FIELDS = ["sprQty", "sprLabor", "sprMaterial", "sprAmount", "sprPct", "sprStored"]
    const input = e.target
    const field = FIELDS.find((f) => input.dataset[f] !== undefined)
    if (!field) return
    const sel = { sprQty: "[data-spr-qty]", sprLabor: "[data-spr-labor]", sprMaterial: "[data-spr-material]", sprAmount: "[data-spr-amount]", sprPct: "[data-spr-pct]", sprStored: "[data-spr-stored]" }
    let target = null

    if (e.key === "ArrowUp" || e.key === "ArrowDown" || e.key === "Enter") {
      const rows = Array.from(this.element.querySelectorAll("tbody tr")).filter((r) => r.querySelector("[data-spr-amount]"))
      const idx = rows.indexOf(input.closest("tr"))
      const next = rows[idx + (e.key === "ArrowUp" ? -1 : 1)]
      target = next?.querySelector(sel[field])
    } else if (e.key === "ArrowLeft" || e.key === "ArrowRight") {
      const atStart = input.selectionStart === 0 && input.selectionEnd === 0
      const atEnd = input.selectionStart === input.value.length && input.selectionEnd === input.value.length
      const i = FIELDS.indexOf(field)
      if (e.key === "ArrowLeft" && atStart && i > 0) {
        target = input.closest("tr").querySelector(sel[FIELDS[i - 1]])
      } else if (e.key === "ArrowRight" && atEnd && i < FIELDS.length - 1) {
        target = input.closest("tr").querySelector(sel[FIELDS[i + 1]])
      }
    } else {
      return
    }

    if (target && !target.disabled) {
      e.preventDefault()
      target.focus()
      target.select()
    }
  }

  // $ inputs settle into money format on blur, qty boxes into plain numbers —
  // commas and cents like the GC grid.
  moneyBlur(e) { e.target.value = e.target.value.trim() === "" ? "" : this._money(this._num(e.target.value)) }
  qtyBlur(e) { e.target.value = e.target.value.trim() === "" ? "" : this._qtyFmt(this._num(e.target.value)) }

  // ── Live math — Qty, $ and % all drive each other (Phelipe 2026-08-17):
  // qty × unit price ↔ $ ↔ % of scheduled. recalc only writes into boxes
  // that aren't being typed in, so nothing fights the cursor.
  amountChanged() { this.recalc(); this._queueSave() }

  qtyChanged(e) {
    const key = e.currentTarget.dataset.sprQty
    const input = this.element.querySelector(`[data-spr-amount][data-key="${key}"]`)
    if (!input) return
    const price = parseFloat(input.dataset.price) || 0
    if (price > 0) input.value = this._money(this._num(e.currentTarget.value) * price)
    this.recalc()
    this._queueSave()
  }

  pctChanged(e) {
    const key = e.currentTarget.dataset.sprPct
    const input = this.element.querySelector(`[data-spr-amount][data-key="${key}"]`)
    if (!input) return
    const pct = parseFloat(e.currentTarget.value.replace(/[%\s]/g, "")) || 0
    const sched = parseFloat(input.dataset.scheduled) || 0
    input.value = this._money((sched * pct) / 100)
    this.recalc()
    this._queueSave()
  }

  recalc() {
    let thisTotal = 0
    let prevTotal = 0
    let storedTotal = 0
    let schedTotal = 0
    let laborTotal = 0
    let materialTotal = 0
    let extraDone = 0
    this.element.querySelectorAll("[data-spr-amount]").forEach((input) => {
      const key = input.dataset.key
      const val = this._num(input.value)
      const sched = parseFloat(input.dataset.scheduled) || 0
      const prev = parseFloat(input.dataset.prev) || 0
      const price = parseFloat(input.dataset.price) || 0
      // Stored materials this period (parity audit PR4) — counts toward
      // completed, exactly like the GC grid.
      const storedBox = this.element.querySelector(`[data-spr-stored="${key}"]`)
      const stored = storedBox ? this._num(storedBox.value) : 0
      thisTotal += val
      prevTotal += prev
      storedTotal += stored
      schedTotal += sched
      // Labor / material apart (S3-9) — only present when the contract splits.
      const laborBox = this.element.querySelector(`[data-spr-labor="${key}"]`)
      const materialBox = this.element.querySelector(`[data-spr-material="${key}"]`)
      laborTotal += laborBox ? this._num(laborBox.value) : 0
      materialTotal += materialBox ? this._num(materialBox.value) : 0
      const pctBox = this.element.querySelector(`[data-spr-pct="${key}"]`)
      if (pctBox && document.activeElement !== pctBox) {
        pctBox.value = sched && val ? ((val / sched) * 100).toFixed(1) : ""
      }
      const qtyBox = this.element.querySelector(`[data-spr-qty="${key}"]`)
      // An added pay item keeps the qty as typed or read off the invoice —
      // its price may be rounded (KC-108), so $ ÷ price would drift.
      const keepQty = input.dataset.extra && qtyBox?.value.trim()
      if (qtyBox && price > 0 && document.activeElement !== qtyBox && !keepQty) {
        qtyBox.value = val ? (val / price).toFixed(2).replace(/\.00$/, "") : ""
      }
      // Qty to date = previously billed qty + this period's (Larry 2026-08-20).
      const prevQty = parseFloat(input.dataset.prevqty) || 0
      const thisQty = qtyBox ? this._num(qtyBox.value) : (price > 0 ? val / price : 0)
      if (prevQty || thisQty) {
        this._set(`[data-spr-totalqty="${key}"]`, this._qtyFmt(prevQty + thisQty))
      }
      const done = prev + val + stored
      // Added pay items (KC-108) are paid on top of the contract — they
      // stay out of the balance to finish and never read as overbilled.
      const extra = !!input.dataset.extra
      if (extra) extraDone += val + stored
      this._set(`[data-spr-total="${key}"]`, `$${this._money(done)}`)
      this._set(`[data-spr-totalpct="${key}"]`, sched ? `${((done / sched) * 100).toFixed(1)}%` : "—")
      this._set(`[data-spr-balance="${key}"]`, `$${this._money(sched - done)}`)
      // Row coloring like the GC grid: red past 100% of scheduled (inverted
      // for negative lines), green when the row is billed this period.
      const row = input.closest("tr")
      if (row) {
        const over = !extra && (sched < 0 ? done < sched - 0.02 : done > sched + 0.02)
        row.classList.toggle("bg-red-50", over)
        row.classList.toggle("bg-green-50", !over && (val !== 0 || stored !== 0))
      }
    })

    const current = this.originalValue + this.netChangeValue
    const completed = prevTotal + thisTotal + storedTotal
    const retainage = (completed * this.retainageValue) / 100
    const earned = completed - retainage
    const due = earned - this.prevCertsValue
    this._set('[data-spr-sum="thisperiod"]', `$${this._money(thisTotal)}`)
    this._set('[data-spr-sum="completed"]', `$${this._money(completed)}`)
    this._set('[data-spr-sum="retainage"]', `$${this._money(retainage)}`)
    this._set('[data-spr-sum="earned"]', `$${this._money(earned)}`)
    this._set('[data-spr-sum="due"]', `$${this._money(Math.max(due, 0))}`)
    this._set('[data-spr-sum="balance"]', `$${this._money(current - completed + extraDone)}`)
    this._set('[data-spr-sum="labor"]', `$${this._money(laborTotal)}`)
    this._set('[data-spr-sum="material"]', `$${this._money(materialTotal)}`)
    this._set('[data-spr-tsum="labor"]', `$${this._money(laborTotal)}`)
    this._set('[data-spr-tsum="material"]', `$${this._money(materialTotal)}`)

    // Grand totals under the grid (parity audit PR3) — live like the GC's.
    this._set('[data-spr-tsum="sched"]', `$${this._money(schedTotal)}`)
    this._set('[data-spr-tsum="prev"]', `$${this._money(prevTotal)}`)
    this._set('[data-spr-tsum="this"]', `$${this._money(thisTotal)}`)
    this._set('[data-spr-tsum="stored"]', `$${this._money(storedTotal)}`)
    this._set('[data-spr-tsum="total"]', `$${this._money(prevTotal + thisTotal + storedTotal)}`)
    this._set('[data-spr-tsum="balance"]', `$${this._money(schedTotal - prevTotal - thisTotal - storedTotal + extraDone)}`)
    this._updatePendingNote()
  }

  storedChanged() { this.recalc(); this._queueSave() }

  // ── KC-108 (Kloepfer): pay items that aren't in the contract ───────────
  // "Add pay item" types one in; the AI read flags the invoice's unmatched
  // lines as pending rows (Approve for payment / Leave out) that stay out
  // of the totals and the save until approved.
  addExtra() {
    const row = this._newExtraRow()
    if (!row) return
    row.querySelector("[data-spr-desc]")?.focus()
    track("Sub Pay Item Added", { sub_contract_id: this._contractId(), source: "manual" })
  }

  _newExtraRow({ description = "", qty = null, unit = "", price = null, amount = null, pending = false } = {}) {
    if (!this.hasExtraTemplateTarget || !this.hasExtraBodyTarget) return null
    const key = `extra-new-${++this._extraSeq}`
    const holder = document.createElement("tbody")
    holder.innerHTML = this.extraTemplateTarget.innerHTML.replaceAll("__KEY__", key)
    const row = holder.querySelector("tr")
    const box = (attr) => row.querySelector(`[${attr}]`)
    box("data-spr-desc").value = description
    box("data-spr-unit").value = unit || ""
    if (price) {
      box("data-spr-price").value = this._money(Number(price))
      box("data-spr-amount").dataset.price = price
    }
    if (qty) box("data-spr-qty").value = this._qtyFmt(Number(qty))
    if (amount) box("data-spr-amount").value = this._money(Number(amount))
    if (pending) this._markPending(row)
    this.extraBodyTarget.appendChild(row)
    this._syncExtraBand()
    this.recalc()
    return row
  }

  // A pending row's $ box drops data-spr-amount, so recalc and the save
  // skip it; its boxes lock until the GC decides.
  _markPending(row) {
    row.dataset.sprPending = "1"
    row.classList.add("bg-amber-50")
    const amount = row.querySelector("[data-spr-amount]")
    amount.removeAttribute("data-spr-amount")
    amount.setAttribute("data-spr-pending-amount", "")
    row.querySelectorAll("input").forEach((i) => { i.disabled = true })
    row.querySelector("[data-spr-pending-actions]")?.classList.replace("hidden", "flex")
    row.querySelector("[data-spr-remove]")?.classList.add("hidden")
    const note = row.querySelector("[data-spr-extra-note]")
    if (note) note.textContent = this.extraTemplateTarget.dataset.pendingLabel
    const total = row.querySelector("[data-spr-total]")
    if (total) total.textContent = "—"
  }

  approveExtra(e) {
    const row = e.currentTarget.closest("tr")
    delete row.dataset.sprPending
    row.classList.remove("bg-amber-50")
    const amount = row.querySelector("[data-spr-pending-amount]")
    amount.removeAttribute("data-spr-pending-amount")
    amount.setAttribute("data-spr-amount", "")
    row.querySelectorAll("input").forEach((i) => { i.disabled = false })
    row.querySelector("[data-spr-pending-actions]")?.remove()
    row.querySelector("[data-spr-remove]")?.classList.remove("hidden")
    const note = row.querySelector("[data-spr-extra-note]")
    if (note) note.textContent = this.extraTemplateTarget.dataset.approvedLabel
    this.recalc()
    this._queueSave()
    track("Sub Invoice Extra Line Decided", { sub_contract_id: this._contractId(), decision: "approved" })
  }

  leaveOutExtra(e) {
    e.currentTarget.closest("tr").remove()
    this._syncExtraBand()
    this.recalc()
    track("Sub Invoice Extra Line Decided", { sub_contract_id: this._contractId(), decision: "left_out" })
  }

  removePayItem(e) {
    e.currentTarget.closest("tr").remove()
    this._syncExtraBand()
    this.recalc()
    this._queueSave()
  }

  extraChanged() { this._queueSave() }

  // The row's own unit price drives $ from qty, like a fee line.
  extraPriceChanged(e) {
    const row = e.currentTarget.closest("tr")
    const amount = row.querySelector("[data-spr-amount]")
    if (!amount) return
    const price = this._num(e.currentTarget.value)
    amount.dataset.price = price
    const qty = this._num(row.querySelector("[data-spr-qty]")?.value)
    if (price > 0 && qty) amount.value = this._money(qty * price)
    this.recalc()
    this._queueSave()
  }

  _syncExtraBand() {
    if (!this.hasExtraBandTarget) return
    this.extraBandTarget.classList.toggle("hidden", !this.extraBodyTarget.querySelector("tr"))
  }

  _updatePendingNote() {
    if (!this.hasPendingNoteTarget) return
    const pending = Array.from(this.element.querySelectorAll("[data-spr-pending-amount]"))
      .reduce((sum, i) => sum + this._num(i.value), 0)
    const has = !!this.element.querySelector("[data-spr-pending]")
    this.pendingNoteTarget.classList.toggle("hidden", !has)
    if (has) this.pendingNoteTarget.textContent = this.pendingNoteTarget.dataset.template.replace("%{amount}", `$${this._money(pending)}`)
  }

  // Drag handles (KC-108): contract lines save their order to the fee
  // schedule; added pay items save with the grid, in the order they sit.
  _initSortables() {
    if (!(this.hasReorderUrlValue && this.reorderUrlValue)) return
    if (this.hasFeeBodyTarget && this.feeBodyTarget.querySelector("[data-spr-fee-line]")) {
      this._feeSortable = Sortable.create(this.feeBodyTarget, {
        handle: "[data-spr-drag]", draggable: "tr[data-spr-fee-line]", animation: 150,
        onEnd: (evt) => { if (evt.oldIndex !== evt.newIndex) this._saveFeeOrder() }
      })
    }
    if (this.hasExtraBodyTarget) {
      this._extraSortable = Sortable.create(this.extraBodyTarget, {
        handle: "[data-spr-drag]", draggable: "tr[data-spr-extra-row]", animation: 150,
        onEnd: (evt) => { if (evt.oldIndex !== evt.newIndex) this._queueSave() }
      })
    }
  }

  disconnect() {
    this._feeSortable?.destroy()
    this._extraSortable?.destroy()
  }

  async _saveFeeOrder() {
    const ids = Array.from(this.feeBodyTarget.querySelectorAll("tr[data-spr-fee-line]")).map((r) => r.dataset.sprFeeLine)
    try {
      const resp = await fetch(this.reorderUrlValue, { method: "POST", headers: this._headers(), body: JSON.stringify({ line_ids: ids }) })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't save the new order — try again")
      this._toast("Line order saved")
    } catch (err) {
      this._toast(err.message, "alert")
    }
  }

  _contractId() {
    return this.reorderUrlValue.match(/sub_contracts\/(\d+)/)?.[1] || null
  }

  // One entry per billed row — fee lines, COs and added pay items (with
  // their own description / unit / price).
  _amountRows() {
    return Array.from(this.element.querySelectorAll("[data-spr-amount]"))
      .map((i) => {
        const key = i.dataset.key
        const box = (attr) => this.element.querySelector(`[${attr}="${key}"]`)?.value.trim() || ""
        const entry = {
          key, amount: i.value.trim(), qty: box("data-spr-qty"), stored: box("data-spr-stored"),
          labor: box("data-spr-labor"), material: box("data-spr-material")
        }
        if (i.dataset.extra) Object.assign(entry, { description: box("data-spr-desc"), unit: box("data-spr-unit"), price: box("data-spr-price") })
        return entry
      })
      // A stored-only row still counts (parity audit PR4).
      .filter((a) => this._num(a.amount) > 0 || this._num(a.stored) > 0)
  }

  // S3-9 (Jason/Starr): labor and material typed apart — the row's $ box
  // (read-only when the contract splits) becomes their sum, then the usual
  // math runs from it.
  splitChanged(e) {
    const key = e.currentTarget.dataset.sprLabor ?? e.currentTarget.dataset.sprMaterial
    const input = this.element.querySelector(`[data-spr-amount][data-key="${key}"]`)
    if (!input) return
    const labor = this.element.querySelector(`[data-spr-labor="${key}"]`)
    const material = this.element.querySelector(`[data-spr-material="${key}"]`)
    const blank = (!labor || labor.value.trim() === "") && (!material || material.value.trim() === "")
    input.value = blank ? "" : this._money(this._num(labor?.value) + this._num(material?.value))
    this.recalc()
    this._queueSave()
  }

  // ── Autosave (GC page only, parity audit PR10) — editing an existing
  // draft/received invoice saves the grid a second after typing stops, like
  // the GC pay app. The portal's plain-form page keeps its explicit save.
  _queueSave() {
    if (!(this.hasUpdateUrlValue && this.updateUrlValue)) return
    if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Saving…"
    clearTimeout(this._saveTimer)
    this._saveTimer = setTimeout(() => this._autoSave(), 1000)
  }

  async _autoSave() {
    const body = {}
    this.element.querySelectorAll("[data-spr-field]").forEach((f) => { if (f.value.trim()) body[f.dataset.sprField] = f.value.trim() })
    body.amounts = this._amountRows()
    if (!body.amounts.length) {
      if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = ""
      return
    }
    try {
      const resp = await fetch(this.updateUrlValue, { method: "PATCH", headers: this._headers(), body: JSON.stringify(body) })
      if (!resp.ok) throw new Error()
      if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Saved ✓"
    } catch {
      if (this.hasSaveStatusTarget) this.saveStatusTarget.textContent = "Couldn't save — check your connection"
    }
  }

  // ── Submit ─────────────────────────────────────────────────────────────
  // Modes (Phelipe 2026-08-18): "draft" parks it, "sign" saves then opens
  // the sign & request flow back on the sub page, "save" updates an
  // existing one in place. The review round-trip is for portal submissions.
  async submit(e) {
    const mode = e?.currentTarget?.dataset?.mode || "draft"
    const existing = this.hasUpdateUrlValue && this.updateUrlValue
    const body = existing ? {} : { mode: mode === "draft" ? "draft" : "received" }
    this.element.querySelectorAll("[data-spr-field]").forEach((f) => { if (f.value.trim()) body[f.dataset.sprField] = f.value.trim() })
    if (this.element.querySelector("[data-spr-pending]")) {
      this._toast("Approve or leave out the flagged invoice line first", "alert")
      return
    }
    body.amounts = this._amountRows()
    if (!body.amounts.length) {
      this._toast("Enter at least one amount in the This period column", "alert")
      return
    }

    if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = true
    try {
      const resp = await fetch(existing ? this.updateUrlValue : this.createUrlValue,
        { method: existing ? "PATCH" : "POST", headers: this._headers(), body: JSON.stringify(body) })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Could not save the pay request")
      const invoiceId = data.id || this.invoiceIdValue
      const files = this._allFiles()
      if (files.length && invoiceId) {
        await fetch(`${this.backUrlValue}/invoices/${invoiceId}/register_files`, {
          method: "POST", headers: this._headers(),
          body: JSON.stringify({ files: files.map((f) => ({ cache_name: f.cache_name, filename: f.filename })) })
        })
      }
      if (mode === "sign" && invoiceId) {
        // The sub page's sign & request modal picks this up on load.
        sessionStorage.setItem("builder-finance.open-sign", String(invoiceId))
        sessionStorage.setItem("builder-finance.flash", "Saved — add your signature to send it for approval")
      } else {
        sessionStorage.setItem("builder-finance.flash",
          mode === "draft" ? "Draft saved — approve it when you're ready" : "Pay request saved")
      }
      window.location = this.backUrlValue
    } catch (err) {
      this._toast(err.message, "alert")
      if (this.hasSubmitBtnTarget) this.submitBtnTarget.disabled = false
    }
  }

  // Delete, behind a confirm modal (Phelipe 2026-08-18) — never a bare
  // browser confirm().
  // Approve & Send pop-up (Phelipe 2026-08-19): the certification only ever
  // appears here, and Approve & Sign stays disabled until it's ticked.
  openSignModal() {
    if (!this.hasSignModalTarget) return
    // The typed signature mirrors the page's "Submitted by" fields — if the
    // sub edited those, the pop-up opens with the same values.
    const pageName = this.element.querySelector('input[name="submitted_name"]')
    const pageEmail = this.element.querySelector('input[name="submitted_email"]')
    const modalName = this.signModalTarget.querySelector('input[name="signed_name"]')
    const modalEmail = this.signModalTarget.querySelector('input[name="signed_email"]')
    if (pageName?.value && modalName) modalName.value = pageName.value
    if (pageEmail?.value && modalEmail) modalEmail.value = pageEmail.value
    this._fillSignSummary()
    this.signModalTarget.classList.remove("hidden")
    this.certToggled()
  }

  // Application summary in the sign modal (parity audit PR12) — the same
  // key numbers the GC sees before sending, refreshed from the live grid.
  _fillSignSummary() {
    const put = (key, text) => {
      const el = this.signModalTarget.querySelector(`[data-sign-sum="${key}"]`)
      if (el) el.textContent = text
    }
    const live = (key) => this.element.querySelector(`[data-spr-sum="${key}"]`)?.textContent || "$0.00"
    put("current", `$${this._money(this.originalValue + this.netChangeValue)}`)
    put("completed", live("completed"))
    put("retainage", live("retainage"))
    put("previous", `$${this._money(this.prevCertsValue)}`)
    put("due", live("due"))
  }

  // "Also send to" rows (Phelipe 2026-08-19) — add/remove extra signers,
  // keeping the little position numbers honest.
  addSignRow() {
    if (!this.hasSignRowsTarget || !this.hasSignRowTemplateTarget) return
    this.signRowsTarget.appendChild(this.signRowTemplateTarget.content.cloneNode(true))
    this._renumberSignRows()
  }

  removeSignRow(e) {
    e.currentTarget.closest("[data-sign-row]")?.remove()
    this._renumberSignRows()
  }

  _renumberSignRows() {
    if (!this.hasSignRowsTarget) return
    this.signRowsTarget.querySelectorAll("[data-sign-pos]").forEach((el, i) => { el.textContent = i + 1 })
  }

  closeSignModal() {
    if (this.hasSignModalTarget) this.signModalTarget.classList.add("hidden")
  }

  certToggled() {
    if (!this.hasSignSubmitTarget || !this.hasCertAgreeTarget) return
    this.signSubmitTarget.disabled = !this.certAgreeTarget.checked
  }

  openDeleteConfirm(e) {
    this._deleteUrl = e.currentTarget.dataset.url
    this.deleteModalTarget.classList.remove("hidden")
  }

  closeDeleteConfirm() { this.deleteModalTarget.classList.add("hidden") }

  stopPropagation(e) { e.stopPropagation() }

  async confirmDelete() {
    if (!this._deleteUrl) return
    this.deleteBtnTarget.disabled = true
    try {
      const resp = await fetch(this._deleteUrl, { method: "DELETE", headers: this._headers() })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Could not delete the pay request")
      sessionStorage.setItem("builder-finance.flash", "Pay request deleted")
      window.location = this.backUrlValue
    } catch (err) {
      this.deleteBtnTarget.disabled = false
      this.closeDeleteConfirm()
      this._toast(err.message, "alert")
    }
  }

  // As-needed backfill (Phelipe 2026-08-31): number + date save on their
  // own even when the rest of a paid invoice is locked.
  async identitySave() {
    if (!this.hasIdentityUrlValue) return
    const val = (f) => this.element.querySelector(`[data-spr-field="${f}"]`)?.value ?? ""
    try {
      const body = { number: val("number"), received_on: val("received_on") }
      const seq = this.element.querySelector('[data-spr-field="order_seq"]')
      if (seq && seq.value.trim() !== "") body.sequence = seq.value.trim()
      const resp = await fetch(this.identityUrlValue, {
        method: "PATCH", headers: this._headers(), body: JSON.stringify(body)
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Couldn't save")
      this._toast("Invoice number & date saved")
    } catch (err) {
      this._toast(err.message, "alert")
    }
  }

  // Pull a pay request back mid-chain (Phelipe 2026-08-18) — voids the
  // signatures and lands back in draft, like the builder pay app.
  withdrawSignatures(e) {
    this._postBack(e.currentTarget.dataset.url, {}, "Signatures withdrawn — back to draft")
  }

  // Payment reminder (Phelipe 2026-08-22) — same button the GC pay app has,
  // nudging the sub about one that's sitting unsigned or unpaid. Stays on the
  // page; a toast reports the send.
  async sendFollowup(e) {
    const btn = e.currentTarget
    const url = btn.dataset.url
    if (!url || btn.disabled) return
    btn.disabled = true
    try {
      const resp = await fetch(url, { method: "POST", headers: this._headers(), body: "{}" })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Could not send the reminder")
      this._toast("Payment reminder sent")
    } catch (err) {
      this._toast(err.message, "alert")
    } finally {
      btn.disabled = false
    }
  }

  // Withdraw asks first (Phelipe 2026-08-22): it voids every signature
  // collected so far, so a stray click shouldn't do it. Same confirm
  // shape as delete. The portal page posts a plain form (no JSON) —
  // _postBack handles the redirect either way.
  openWithdrawConfirm(e) {
    this._withdrawUrl = e.currentTarget.dataset.url
    if (this.hasWithdrawModalTarget) this.withdrawModalTarget.classList.remove("hidden")
  }

  closeWithdrawConfirm() {
    if (this.hasWithdrawModalTarget) this.withdrawModalTarget.classList.add("hidden")
  }

  confirmWithdraw() {
    if (!this._withdrawUrl) return
    if (this.hasWithdrawBtnTarget) this.withdrawBtnTarget.disabled = true

    // The GC page has a JSON endpoint; the portal's redirects with its own
    // flash message, so a plain form post is the dependable way there —
    // the page lands back on the list with that message showing.
    if (this.hasUpdateUrlValue && this.updateUrlValue) {
      this._postBack(this._withdrawUrl, {}, "Signatures withdrawn — back to draft")
        .finally(() => { if (this.hasWithdrawBtnTarget) this.withdrawBtnTarget.disabled = false })
      return
    }
    const form = document.createElement("form")
    form.method = "post"
    form.action = this._withdrawUrl
    form.dataset.turbo = "false"
    const token = document.createElement("input")
    token.type = "hidden"
    token.name = "authenticity_token"
    token.value = document.querySelector('meta[name="csrf-token"]')?.content || ""
    form.appendChild(token)
    // Member subs post from inside the project shell — keep them there
    // afterwards instead of bouncing to the tokened portal.
    if (this.element.querySelector('input[name="in_app"]')) {
      const inApp = document.createElement("input")
      inApp.type = "hidden"
      inApp.name = "in_app"
      inApp.value = "1"
      form.appendChild(inApp)
    }
    document.body.appendChild(form)
    form.submit()
  }


  // Period end preselects to the invoice date (Phelipe 2026-08-21) — typing
  // or AI-filling the invoice date drags period end along with it; the sub
  // can still change period end afterwards.
  invoiceDateChanged(e) {
    const iso = e.currentTarget.value
    if (iso) this._setDate("period_end", iso)
  }

  // Set a flatpickr-wrapped date input (plain value assignment leaves the
  // picker's internal state behind).
  _setDate(field, iso) {
    if (!iso) return
    const el = this.element.querySelector(`[data-spr-field="${field}"]`)
    if (!el) return
    if (el._flatpickr) el._flatpickr.setDate(iso, true, "Y-m-d")
    else el.value = iso
  }

  // Quick decisions from the page's three-dots (Phelipe 2026-08-18).
  async quickApprove(e) {
    await this._postBack(e.currentTarget.dataset.url, {}, "Pay request approved")
  }

  // ── Revise & resubmit (Phelipe 2026-08-27) ────────────────────────────
  // The GC sends the pay request back with a comment. The comment is the
  // point of the flow — the sub can't act on "rejected" alone — so the
  // Send button stays disabled until something is typed.
  openReviseModal(e) {
    this._reviseUrl = e.currentTarget.dataset.url
    if (!this.hasReviseModalTarget) return
    if (this.hasReviseCommentTarget) this.reviseCommentTarget.value = ""
    if (this.hasReviseErrorTarget) this.reviseErrorTarget.classList.add("hidden")
    this._syncReviseBtn()
    this.reviseModalTarget.classList.remove("hidden")
    if (this.hasReviseCommentTarget) setTimeout(() => this.reviseCommentTarget.focus(), 50)
  }

  closeReviseModal() {
    if (this.hasReviseModalTarget) this.reviseModalTarget.classList.add("hidden")
  }

  reviseCommentChanged() { this._syncReviseBtn() }

  _syncReviseBtn() {
    if (!this.hasReviseBtnTarget) return
    const filled = this.hasReviseCommentTarget && this.reviseCommentTarget.value.trim().length > 0
    this.reviseBtnTarget.disabled = !filled
    this.reviseBtnTarget.classList.toggle("opacity-50", !filled)
    this.reviseBtnTarget.classList.toggle("cursor-not-allowed", !filled)
  }

  async confirmRevise() {
    if (!this._reviseUrl) return
    const comment = this.hasReviseCommentTarget ? this.reviseCommentTarget.value.trim() : ""
    if (!comment) return

    if (this.hasReviseBtnTarget) this.reviseBtnTarget.disabled = true
    try {
      const resp = await fetch(this._reviseUrl, {
        method: "POST", headers: this._headers(), body: JSON.stringify({ comment })
      })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "That didn't work")
      sessionStorage.setItem("builder-finance.flash",
        data.emailed ? "Sent back to the sub with your comments" : "Sent back to the sub — no email on file")
      window.location = this.backUrlValue
    } catch (err) {
      if (this.hasReviseErrorTarget) {
        this.reviseErrorTarget.textContent = err.message
        this.reviseErrorTarget.classList.remove("hidden")
      }
      if (this.hasReviseBtnTarget) this.reviseBtnTarget.disabled = false
    }
  }

  rowAction(e) {
    this._postBack(e.currentTarget.dataset.url, {}, e.currentTarget.dataset.flash || "Done")
  }

  async _postBack(url, body, flash) {
    try {
      const resp = await fetch(url, { method: "POST", headers: this._headers(), body: JSON.stringify(body) })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "That didn't work")
      sessionStorage.setItem("builder-finance.flash", flash)
      window.location = this.backUrlValue
    } catch (err) {
      this._toast(err.message, "alert")
    }
  }

  // ── AI zone (same plumbing as the quick modal) ─────────────────────────
  clickZone() { this.fileInputTarget.click() }
  dragOver(e) { e.preventDefault(); this.dropZoneTarget.classList.add("border-purple-500", "bg-purple-200") }
  dragLeave() { this.dropZoneTarget.classList.remove("border-purple-500", "bg-purple-200") }
  dropFile(e) {
    e.preventDefault()
    this.dragLeave()
    const file = e.dataTransfer?.files?.[0]
    if (file) this._upload(file)
  }
  fileChosen(e) {
    const file = e.target.files[0]
    if (file) this._upload(file)
    e.target.value = ""
  }

  async _upload(file) {
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 bg-purple-100 text-purple-800 rounded-lg px-2.5 py-1 text-[13px]"
    chip.innerHTML = `<svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg><span class="max-w-[180px] truncate">${esc(file.name)}</span>`
    this.chipsTarget.innerHTML = ""
    this.chipsTarget.appendChild(chip)
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type, byte_size: file.size }] })
      })
      const data = await presign.json()
      if (!presign.ok) throw new Error(data.error || "Could not start the upload")
      const u = data.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed — try again")
      this._cache = { cache_name: u.cache_name, filename: file.name }
      this._syncCacheToForm()
      this._syncAiChipToAttachments()
      chip.innerHTML = `<svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/></svg><span class="max-w-[180px] truncate">${esc(file.name)}</span>`
      await this._read()
    } catch (err) {
      this.chipsTarget.innerHTML = ""
      this._toast(err.message, "alert")
    }
  }

  async _read() {
    if (!this._cache) return
    const status = this.readStatusTarget
    status.textContent = "✨ Reading the invoice…"
    status.classList.remove("hidden")
    try {
      const resp = await fetch(this.readUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ cache_name: this._cache.cache_name, filename: this._cache.filename })
      })
      const data = await resp.json()
      if (!resp.ok) throw new Error(data.error || "Couldn't read the invoice — fill the grid manually")
      const set = (f, v) => { const el = this.element.querySelector(`[data-spr-field="${f}"]`); if (el && v) el.value = v }
      set("number", data.number)
      // Dates go through the picker (plain value writes leave flatpickr
      // stale). Period end preselects to the invoice date when the AI finds
      // one (Phelipe 2026-08-21) — the sub can still change it after.
      this._setDate("period_end", data.invoice_date || data.period_end)
      // "Received on" defaults to today; the invoice's own date wins when
      // the AI finds one (Larry 2026-08-18 — a July invoice read "today").
      this._setDate("received_on", data.invoice_date || data.period_end)
      // The portal page's Invoice date box (Phelipe 2026-08-21) — only the
      // date actually printed on the invoice, never a period fallback.
      this._setDate("invoice_date", data.invoice_date)
      const amounts = Array.isArray(data.amounts) ? data.amounts : []
      amounts.forEach((a) => {
        const input = this.element.querySelector(`[data-spr-amount][data-key="${a.key}"]`)
        if (input) input.value = this._money(Number(a.amount))
      })
      // KC-108: invoice lines the contract doesn't have land flagged at the
      // bottom for the GC's OK. A re-read replaces the earlier flags.
      this.element.querySelectorAll("tr[data-spr-pending]").forEach((r) => r.remove())
      const extras = Array.isArray(data.extras) ? data.extras : []
      const flagged = extras.filter((x) => this._newExtraRow({
        description: x.description, qty: x.quantity, unit: x.unit, price: x.unit_price, amount: x.amount, pending: true
      })).length
      if (flagged) track("Sub Invoice Extra Lines Flagged", { sub_contract_id: this._contractId(), lines: flagged })
      // As-needed invoices bill COMPLETE (Larry/Phelipe 2026-08-27): with
      // no fee schedule behind them, each line item is 100% done — every
      // still-empty line fills with its full remaining value. Amounts the
      // AI matched above stay as read.
      let filledComplete = 0
      if (this.asNeededValue) {
        this.element.querySelectorAll("[data-spr-amount][data-key]").forEach((input) => {
          if (input.value.trim()) return
          const sched = parseFloat(input.dataset.scheduled) || 0
          const prev = parseFloat(input.dataset.prev) || 0
          const remaining = sched - prev
          if (remaining <= 0) return
          input.value = this._money(remaining)
          filledComplete++
        })
      }
      this._syncExtraBand()
      this.recalc()
      status.textContent = filledComplete
        ? `✓ Read the invoice — ${filledComplete} ${filledComplete === 1 ? "line" : "lines"} billed complete (100%), review before approving`
        : amounts.length
          ? `✓ Matched ${amounts.length} ${amounts.length === 1 ? "line" : "lines"} — review each amount before adding`
          : "✓ Read the invoice — fill the billing column and review"
      if (flagged) {
        status.textContent += ` · ${flagged} ${flagged === 1 ? "line on the invoice isn't" : "lines on the invoice aren't"} in the contract — added below for your OK`
      }
    } catch (err) {
      status.textContent = err.message
    }
  }

  // ── Attachments box (parity audit PR11) — extra files accumulate as
  // chips beside the AI-read invoice, exactly like the GC pay app's
  // attachments: presign → S3 PUT → chip with a × to pull it back out.
  attachClick() { if (this.hasAttachInputTarget) this.attachInputTarget.click() }

  attachChosen(e) {
    Array.from(e.target.files || []).forEach((file) => this._uploadExtra(file))
    e.target.value = ""
  }

  attachDragOver(e) { e.preventDefault(); e.currentTarget.classList.add("border-blue-500", "bg-blue-50") }
  attachDragLeave(e) { e.currentTarget.classList.remove("border-blue-500", "bg-blue-50") }
  attachDrop(e) {
    e.preventDefault()
    this.attachDragLeave(e)
    Array.from(e.dataTransfer?.files || []).forEach((file) => this._uploadExtra(file))
  }

  async _uploadExtra(file) {
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 bg-blue-100 text-blue-800 rounded-lg px-2.5 py-1 text-[13px]"
    chip.innerHTML = `<svg class="w-3.5 h-3.5 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z"></path></svg><span class="max-w-[180px] truncate">${esc(file.name)}</span>`
    this.attachChipsTarget.appendChild(chip)
    try {
      const presign = await fetch(this.presignUrlValue, {
        method: "POST", headers: this._headers(),
        body: JSON.stringify({ files: [{ filename: file.name, content_type: file.type, byte_size: file.size }] })
      })
      const data = await presign.json()
      if (!presign.ok) throw new Error(data.error || "Could not start the upload")
      const u = data.uploads[0]
      const put = await fetch(u.upload_url, { method: "PUT", headers: { "Content-Type": u.content_type }, body: file })
      if (!put.ok) throw new Error("Upload failed — try again")
      this._extraFiles.push({ cache_name: u.cache_name, filename: file.name })
      chip.className = "inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-lg px-2.5 py-1 text-[13px]"
      chip.dataset.cacheName = u.cache_name
      chip.innerHTML = `<svg class="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/></svg><span class="max-w-[180px] truncate">${esc(file.name)}</span><button type="button" class="text-gray-400 hover:text-gray-600 font-bold leading-none" data-action="click->sub-pay-request#removeExtra">&times;</button>`
      this._syncCacheToForm()
    } catch (err) {
      chip.remove()
      this._toast(err.message, "alert")
    }
  }

  removeExtra(e) {
    const chip = e.currentTarget.closest("span")
    const cacheName = chip?.dataset.cacheName
    if (chip?.dataset.aiFile !== undefined) {
      // Pulling the AI-read invoice out of Attachments clears the purple
      // zone too — one file, one state.
      this._cache = null
      if (this.hasChipsTarget) this.chipsTarget.innerHTML = ""
      if (this.hasReadStatusTarget) this.readStatusTarget.classList.add("hidden")
    }
    this._extraFiles = this._extraFiles.filter((f) => f.cache_name !== cacheName)
    chip?.remove()
    this._syncCacheToForm()
  }

  // ── Saved files (Phelipe 2026-08-28) — a file already ON the application
  // comes off through the server, unlike removeExtra's in-session chips.
  // Open until the pay request is fully approved.
  removeSavedFile(e) {
    this._removeFileUrl = e.currentTarget.dataset.url
    if (this.hasRemoveFileNameTarget) this.removeFileNameTarget.textContent = e.currentTarget.dataset.name || "This file"
    this.removeFileModalTarget.classList.remove("hidden")
  }

  closeRemoveFile() {
    this.removeFileModalTarget.classList.add("hidden")
    this._removeFileUrl = null
  }

  // The History Log is rendered server-side, so the page comes back to show
  // the new row with its link to the removed file — same sessionStorage
  // flash the other reload-needing actions use.
  async confirmRemoveFile() {
    if (!this._removeFileUrl) return
    this.removeFileBtnTarget.disabled = true
    try {
      const resp = await fetch(this._removeFileUrl, { method: "DELETE", headers: this._headers() })
      const data = await resp.json().catch(() => ({}))
      if (!resp.ok) throw new Error(data.error || "Could not remove the file")
      sessionStorage.setItem("builder-finance.flash", "File removed")
      window.location.reload()
    } catch (err) {
      this.removeFileBtnTarget.disabled = false
      this.closeRemoveFile()
      this._toast(err.message, "alert")
    }
  }

  // The AI-read invoice also shows in the Attachments list (Phelipe
  // 2026-08-21) — the bottom section is the one place listing everything
  // that rides the application. Re-dropping replaces the previous AI chip.
  _syncAiChipToAttachments() {
    if (!this.hasAttachChipsTarget || !this._cache) return
    const esc = (s) => (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]))
    this.attachChipsTarget.querySelector("[data-ai-file]")?.remove()
    const chip = document.createElement("span")
    chip.className = "inline-flex items-center gap-1.5 bg-gray-100 text-gray-700 rounded-lg px-2.5 py-1 text-[13px]"
    chip.dataset.aiFile = ""
    chip.dataset.cacheName = this._cache.cache_name
    chip.innerHTML = `<svg class="w-3.5 h-3.5 text-gray-400" fill="currentColor" viewBox="0 0 20 20"><path fill-rule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clip-rule="evenodd"/></svg><span class="max-w-[180px] truncate">${esc(this._cache.filename)}</span><button type="button" class="text-gray-400 hover:text-gray-600 font-bold leading-none" data-action="click->sub-pay-request#removeExtra">&times;</button>`
    this.attachChipsTarget.prepend(chip)
  }

  // AI-read invoice + every extra attachment, in upload order.
  _allFiles() {
    return (this._cache ? [this._cache] : []).concat(this._extraFiles || [])
  }

  // The portal page submits a plain form (no createUrl) — every file rides
  // along as cache_names[]/filenames[] hidden inputs, which the server
  // registers onto the pay application (Larry 2026-08-20: the doc that
  // pre-filled it must end up ATTACHED to it).
  _syncCacheToForm() {
    const form = document.getElementById("sub-pay-request-form")
    if (!form) return
    form.querySelectorAll("[data-spr-cache]").forEach(el => el.remove())
    const add = (name, value) => {
      const input = document.createElement("input")
      input.type = "hidden"
      input.name = name
      input.value = value
      input.setAttribute("data-spr-cache", "")
      form.appendChild(input)
    }
    this._allFiles().forEach((f) => {
      add("cache_names[]", f.cache_name)
      add("filenames[]", f.filename)
    })
  }

  // ── Helpers ────────────────────────────────────────────────────────────
  _num(s) { return parseFloat(String(s).replace(/[$,%\s]/g, "")) || 0 }
  _qtyFmt(n) { return n.toFixed(2).replace(/\.?0+$/, "") || "0" }
  _money(n) { return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }
  // Updates EVERY match — the G702 numbers render twice (summary card on
  // top + totals card under the grid), so both stay live.
  _set(sel, text) { this.element.querySelectorAll(sel).forEach(el => { el.textContent = text }) }
  _headers() {
    return { "Content-Type": "application/json", "Accept": "application/json",
             "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content }
  }
  _toast(message, type = "notice") {
    const div = document.createElement("div")
    div.className = type === "alert" ? "flash-toast-alert" : "flash-toast-notice"
    div.textContent = message
    div.setAttribute("data-controller", "flash")
    div.setAttribute("data-flash-dismiss-after-value", "5000")
    document.body.prepend(div)
  }
}
